import { useEffect, useState } from "react";
import { Link } from "react-router";
import { ErrorNotice, useAction } from "../components/ui";
import { api, type MoneyByCurrency, type Report } from "../lib/api";
import { addDays, todayInLondon } from "../lib/dates";
import { formatMoneyByCurrency } from "./DeskSections";

type Preset = "month" | "last-month" | "quarter" | "year" | "12-months";
const PRESETS: { id: Preset; label: string }[] = [
  { id: "month", label: "This month" },
  { id: "last-month", label: "Last month" },
  { id: "quarter", label: "This quarter" },
  { id: "year", label: "This year" },
  { id: "12-months", label: "Last 12 months" },
];

const pad2 = (n: number) => String(n).padStart(2, "0");
const firstOfMonth = (y: number, m: number) => `${y}-${pad2(m)}-01`;

/** The UK-calendar dates a preset covers, up to and including today. */
export function presetRange(preset: Preset, today = todayInLondon()): { from: string; to: string } {
  const [y, m] = today.split("-").map(Number) as [number, number];
  switch (preset) {
    case "month":
      return { from: firstOfMonth(y, m), to: today };
    case "last-month": {
      const [ly, lm] = m === 1 ? [y - 1, 12] : [y, m - 1];
      return { from: firstOfMonth(ly, lm), to: addDays(firstOfMonth(y, m), -1) };
    }
    case "quarter":
      return { from: firstOfMonth(y, Math.floor((m - 1) / 3) * 3 + 1), to: today };
    case "year":
      return { from: `${y}-01-01`, to: today };
    case "12-months":
      return { from: m === 12 ? `${y}-01-01` : firstOfMonth(y - 1, m + 1), to: today };
  }
}

const monthName = new Intl.DateTimeFormat("en-GB", { month: "short", year: "numeric", timeZone: "UTC" });
const longDay = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
const fmtDay = (d: string) => longDay.format(new Date(`${d}T00:00:00Z`));
const percent = (n: number, of: number) => (of ? `${Math.round((n / of) * 100)}%` : "—");

/** Fees, activity and conversion over a period, for the whole agency. */
export function Reports() {
  const [preset, setPreset] = useState<Preset | "custom">("quarter");
  const [range, setRange] = useState(presetRange("quarter"));
  const [report, setReport] = useState<Report | null>(null);
  const load = useAction();

  useEffect(() => {
    if (!range.from || !range.to) return;
    void load.run(async () => setReport(await api.report(range.from, range.to)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to]);

  const choose = (p: Preset) => {
    setPreset(p);
    setRange(presetRange(p));
  };

  return (
    <>
      <div className="page-head">
        <p className="label">Reports</p>
        <h1 className="display">
          How the desk is <em>doing</em>.
        </h1>
        <p className="lede">Fees, who did what, and how far candidates get. Placements count from the day they were recorded.</p>
      </div>

      <section className="section" aria-labelledby="period-h">
        <h2 id="period-h" className="sr-only">
          Period
        </h2>
        <fieldset className="segmented spaced">
          <legend className="sr-only">Period</legend>
          {PRESETS.map((p) => (
            <label key={p.id}>
              <input type="radio" name="period" checked={preset === p.id} onChange={() => choose(p.id)} />
              <span>{p.label}</span>
            </label>
          ))}
        </fieldset>
        <div className="grid-3 spaced-top">
          <label className="field">
            <span className="label">From</span>
            <input
              type="date"
              value={range.from}
              max={range.to}
              onChange={(e) => {
                setPreset("custom");
                setRange((r) => ({ ...r, from: e.target.value }));
              }}
            />
          </label>
          <label className="field">
            <span className="label">To</span>
            <input
              type="date"
              value={range.to}
              min={range.from}
              onChange={(e) => {
                setPreset("custom");
                setRange((r) => ({ ...r, to: e.target.value }));
              }}
            />
          </label>
        </div>
        <ErrorNotice error={load.error} />
      </section>

      {report && <ReportBody report={report} />}
    </>
  );
}

function ReportBody({ report: r }: { report: Report }) {
  const f = r.funnel;
  const maxMonth = Math.max(1, ...r.byMonth.map((m) => m.placements));
  return (
    <>
      <section className="section" aria-labelledby="totals-h">
        <div className="section-head">
          <h2 id="totals-h" className="label">
            {fmtDay(r.from)} to {fmtDay(r.to)}
          </h2>
          <button type="button" className="link-btn" onClick={() => downloadCsv(r)}>
            Download as a spreadsheet
          </button>
        </div>
        <dl className="ledger">
          <div>
            <dt className="label">Fees</dt>
            <dd className="figure-xl">{formatMoneyByCurrency(r.totals.fees)}</dd>
          </div>
          <div>
            <dt className="label">Placements</dt>
            <dd className="figure-xl">{r.totals.placements}</dd>
          </div>
          <div>
            <dt className="label">Days to fill</dt>
            <dd className="figure-xl">{r.totals.averageDaysToFill ?? "—"}</dd>
            <dd className="secondary">on average, from the job opening to the placement</dd>
          </div>
        </dl>
      </section>

      {r.byMonth.length > 1 && (
        <section className="section" aria-labelledby="months-h">
          <div className="section-head">
            <h2 id="months-h" className="label">
              By month
            </h2>
          </div>
          <ol className="bars">
            {r.byMonth.map((m) => (
              <li key={m.month}>
                <span className="bar-label">{monthName.format(new Date(`${m.month}-01T00:00:00Z`))}</span>
                <span className="bar-track" aria-hidden>
                  <span className="bar" style={{ width: `${(m.placements / maxMonth) * 100}%` }} />
                </span>
                <span className="figures bar-value">
                  {m.placements} · {formatMoneyByCurrency(m.fees)}
                </span>
              </li>
            ))}
          </ol>
        </section>
      )}

      <section className="section" aria-labelledby="who-h">
        <div className="section-head">
          <h2 id="who-h" className="label">
            Who did what
          </h2>
        </div>
        <div className="team-table" tabIndex={0} role="region" aria-label="Who did what">
          <table>
            <thead>
              <tr>
                <th className="label">Who</th>
                <th className="label num-col">Fees</th>
                <th className="label num-col">Placed</th>
                <th className="label num-col">Jobs</th>
                <th className="label num-col">Added</th>
                <th className="label num-col">Put forward</th>
                <th className="label num-col">Interviews</th>
                <th className="label num-col">Client calls</th>
                <th className="label num-col">Notes</th>
              </tr>
            </thead>
            <tbody>
              {r.byConsultant.map((c) => (
                <tr key={c.id}>
                  <td>{c.name}</td>
                  <td className="figures num-col" data-label="Fees">{formatMoneyByCurrency(c.fees)}</td>
                  <td className="figures num-col" data-label="Placed">{c.placements}</td>
                  <td className="figures num-col" data-label="Jobs opened">{c.jobsOpened}</td>
                  <td className="figures num-col" data-label="Candidates added">{c.candidatesAdded}</td>
                  <td className="figures num-col" data-label="Put forward">{c.putForward}</td>
                  <td className="figures num-col" data-label="Interviews">{c.interviewsBooked}</td>
                  <td className="figures num-col" data-label="Client calls">{c.clientActivities}</td>
                  <td className="figures num-col" data-label="Cand. notes">{c.candidateNotes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="hint">Fees and placements go to whoever owns the job. Jobs: opened. Added: candidates added. Interviews: booked. Client calls: calls, emails, meetings and notes logged on companies. Notes: on candidates.</p>
      </section>

      <section className="section" aria-labelledby="funnel-h">
        <div className="section-head">
          <h2 id="funnel-h" className="label">
            Candidates put forward in this period
          </h2>
          <span className="label figures">{f.putForward}</span>
        </div>
        {f.putForward === 0 ? (
          <p className="empty-line">Nobody was put forward for a job in this period.</p>
        ) : (
          <>
            <ol className="bars">
              {[
                { label: "Put forward", n: f.putForward },
                { label: "Into interviews", n: f.interviewed },
                { label: "Reached offer", n: f.offered },
                { label: "Placed", n: f.placed },
              ].map((s) => (
                <li key={s.label}>
                  <span className="bar-label">{s.label}</span>
                  <span className="bar-track" aria-hidden>
                    <span className="bar" style={{ width: `${(s.n / f.putForward) * 100}%` }} />
                  </span>
                  <span className="figures bar-value">
                    {s.n} · {percent(s.n, f.putForward)}
                  </span>
                </li>
              ))}
            </ol>
            <p className="secondary">
              Of them, {f.active} still in process, {f.rejected} rejected and {f.withdrawn} withdrew. “Reached offer” counts stages with “offer” in
              the name.
            </p>
          </>
        )}
      </section>

      <div className="grid-2">
        <section className="section" aria-labelledby="clients-h">
          <div className="section-head">
            <h2 id="clients-h" className="label">
              Clients placed with
            </h2>
          </div>
          {r.byClient.length === 0 ? (
            <p className="empty-line">No placements in this period.</p>
          ) : (
            <ol className="index">
              {r.byClient.map((c, i) => (
                <li key={c.id}>
                  <span className="num" aria-hidden>
                    {pad2(i + 1)}
                  </span>
                  <span className="main">
                    <Link className="primary" to={`/companies/${c.id}`}>
                      {c.name}
                    </Link>
                  </span>
                  <span className="actions figures">
                    {c.placements} · {formatMoneyByCurrency(c.fees)}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </section>

        <section className="section" aria-labelledby="sources-h">
          <div className="section-head">
            <h2 id="sources-h" className="label">
              Where candidates came from
            </h2>
          </div>
          {r.bySource.length === 0 ? (
            <p className="empty-line">No candidates added or placed in this period.</p>
          ) : (
            <ol className="index">
              {r.bySource.map((s, i) => (
                <li key={s.source}>
                  <span className="num" aria-hidden>
                    {pad2(i + 1)}
                  </span>
                  <span className="main">
                    <span className="primary">{s.source}</span>
                  </span>
                  <span className="actions figures">
                    {s.added} added · {s.placed} placed
                  </span>
                </li>
              ))}
            </ol>
          )}
          <p className="hint">Placed counts placements in the period, whenever the candidate was added.</p>
        </section>
      </div>
    </>
  );
}

const money = (m: MoneyByCurrency) =>
  Object.entries(m)
    .map(([c, v]) => `${c} ${v}`)
    .join(" + ");

/** The report as a CSV file, one table after another, for a spreadsheet. */
export function reportCsv(r: Report): string {
  const cell = (v: string | number) => (typeof v === "number" ? String(v) : /[",\n]/.test(v) || /^[=+\-@]/.test(v) ? `"${v.replace(/"/g, '""').replace(/^([=+\-@])/, "'$1")}"` : v);
  const rows: (string | number)[][] = [
    [`Report ${r.from} to ${r.to}`],
    [],
    ["Fees", money(r.totals.fees)],
    ["Placements", r.totals.placements],
    ["Average days to fill", r.totals.averageDaysToFill ?? ""],
    [],
    ["Who", "Fees", "Placed", "Jobs opened", "Candidates added", "Put forward", "Interviews booked", "Client calls and notes", "Candidate notes"],
    ...r.byConsultant.map((c) => [c.name, money(c.fees), c.placements, c.jobsOpened, c.candidatesAdded, c.putForward, c.interviewsBooked, c.clientActivities, c.candidateNotes]),
    [],
    ["Month", "Placements", "Fees"],
    ...r.byMonth.map((m) => [m.month, m.placements, money(m.fees)]),
    [],
    ["Put forward", "Into interviews", "Reached offer", "Placed", "Rejected", "Withdrew", "Still in process"],
    [r.funnel.putForward, r.funnel.interviewed, r.funnel.offered, r.funnel.placed, r.funnel.rejected, r.funnel.withdrawn, r.funnel.active],
    [],
    ["Client", "Placements", "Fees"],
    ...r.byClient.map((c) => [c.name, c.placements, money(c.fees)]),
    [],
    ["Source", "Added", "Placed"],
    ...r.bySource.map((s) => [s.source, s.added, s.placed]),
  ];
  return rows.map((row) => row.map(cell).join(",")).join("\r\n") + "\r\n";
}

function downloadCsv(r: Report) {
  const url = URL.createObjectURL(new Blob(["﻿" + reportCsv(r)], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `kudurms-report-${r.from}-to-${r.to}.csv`;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
